import React from "react";
import Select from "react-select";

export type Option = { label: string; value: string | number };

type Props = {
  label?: string;
  options?: Option[];
  value?: Option | null;
  onChange?: (v: Option | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  className?: string;
  required?: boolean;
  compact?: boolean;
  width?: number | string | null;
};

const resolveWidth = (w?: number | string | null) =>
  w == null ? undefined : typeof w === "number" ? `${w}px` : w;

const buildStyles = (compact: boolean, width?: number | string | null) => {
  const height = compact ? 28 : 34;
  const fontSize = compact ? 10 : 13;
  const optionPadding = compact ? "6px 8px" : "8px 10px";
  const menuPortalZ = 9999;
  const resolvedWidth = resolveWidth(width);
  const textColor = "#111827";

  return {
    container: (provided: any) => ({
      ...provided,
      width: resolvedWidth ?? provided.width,
      minWidth: resolvedWidth ?? provided.minWidth,
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      color: textColor,
      background: state.isDisabled ? "#FAFAFA" : "#fff",
      borderColor: state.isFocused ? "#6366F1" : "#E5E7EB",
      minHeight: `${height}px`,
      height: `${height}px`,
      fontSize,
      boxShadow: state.isFocused
        ? `0 0 0 3px ${"rgba(99,102,241,0.08)"}`
        : provided.boxShadow,
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: `0 8px`,
      height: `${height}px`,
      fontSize,
    }),
    input: (provided: any) => ({
      ...provided,
      margin: 0,
      fontSize,
      color: textColor,
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: `${height}px`,
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: menuPortalZ }),
    menu: (provided: any) => ({
      ...provided,
      minWidth: resolvedWidth ?? provided.minWidth,
      width: resolvedWidth ?? provided.width,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize,
      padding: optionPadding,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      fontSize,
      color: textColor,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      fontSize,
      color: textColor,
    }),
  };
};

const SingleSelectInput: React.FC<Props> = ({
  label,
  options = [],
  value = null,
  onChange,
  placeholder = "Select…",
  isClearable = true,
  isDisabled = false,
  className = "",
  required = false,
  compact = false,
  width = null,
}) => {
  const styles = React.useMemo(
    () => buildStyles(compact, width),
    [compact, width]
  );

  const containerStyle: React.CSSProperties = {};
  const resolvedWidth = resolveWidth(width);
  if (resolvedWidth) containerStyle.width = resolvedWidth;

  return (
    <div className={`space-y-1 text-xs ${className}`} style={containerStyle}>
      {label ? (
        <label className="block text-xs font-medium text-gray-700">
          {required && <span className="text-red-500 mr-1">*</span>}
          {label}
        </label>
      ) : null}

      <Select<Option, false>
        options={options}
        value={value}
        onChange={(opt) => {
          onChange?.(opt as Option | null);
        }}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        styles={styles}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        isSearchable
        classNamePrefix="single-select"
      />
    </div>
  );
};

export default SingleSelectInput;
